import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchChatbots, selectChatbot } from "../redux/store/chatbotSlice";
import { getUser } from "../utils/auth";
import { api } from "../services/api";
import {
  FiGrid, FiLink, FiFileText, FiType, FiDroplet, FiSettings,
  FiChevronDown, FiMonitor, FiInbox, FiBarChart2, FiUsers,
  FiZap, FiYoutube, FiBook, FiHeadphones, FiCheck, FiPlus,
  FiChevronRight, FiUserPlus,
} from "react-icons/fi";
import { SiNotion } from "react-icons/si";

const ADMIN_ROLES = ["OWNER", "ADMIN"];

// Map each nav item to its permission key (matches AppPage enum on backend)
const NAV_ITEMS = [
  { page: "dashboard",    to: (id) => `/overview/${id}`,                icon: FiGrid,      label: "Overview"   },
  { page: "monitoring",   to: (id) => `/monitoring/${id}`,              icon: FiMonitor,   label: "Monitoring" },
  { page: "inbox",        to: (id) => `/inbox/${id}`,                   icon: FiInbox,     label: "Inbox"      },
  { page: "analytics",    to: (id) => `/analytics/${id}`,               icon: FiBarChart2, label: "Analytics"  },
  { page: "leads",        to: (id) => `/leads/${id}`,                   icon: FiUsers,     label: "Leads"      },
];

const SOURCE_ITEMS = [
  { to: (id) => `/sources/website/${id}`,     icon: FiLink,       label: "Website"    },
  { to: (id) => `/sources/files/${id}`,       icon: FiFileText,   label: "Files"      },
  { to: (id) => `/sources/raw/${id}`,         icon: FiType,       label: "Raw Text"   },
  { to: (id) => `/sources/notion/${id}`,      icon: SiNotion,     label: "Notion"     },
  { to: (id) => `/sources/youtube/${id}`,     icon: FiYoutube,    label: "YouTube"    },
  { to: (id) => `/sources/confluence/${id}`,  icon: FiBook,       label: "Confluence" },
  { to: (id) => `/sources/zendesk/${id}`,     icon: FiHeadphones, label: "Zendesk"    },
];

const CUSTOMIZE_ITEMS = [
  { page: "appearance",   to: (id) => `/customisation/appearance/${id}`, icon: FiDroplet, label: "Appearance" },
];

const OTHER_ITEMS = [
  { page: "integrations", to: (id) => `/integrations/${id}`, icon: FiZap,      label: "Integrations" },
  { page: "settings",     to: (id) => `/advanced/settings/${id}`, icon: FiSettings, label: "Settings" },
];

function canView(page, user) {
  if (!page) return true;
  if (ADMIN_ROLES.includes(user?.role)) return true;
  return (user?.permissions || []).includes(page);
}

export default function Sidebar() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const dispatch  = useDispatch();
  const { chatbots, loading, selected } = useSelector((s) => s.chatbots);
  const [open, setOpen]         = useState(false);
  const [contentOpen, setContent] = useState(true);
  const [usage, setUsage]       = useState(null);
  const ref = useRef(null);
  const params = useParams();
  const user = getUser();
  const isAdmin = ADMIN_ROLES.includes(user?.role);

  // Fetch chatbots on mount
  useEffect(() => {
    dispatch(fetchChatbots());
  }, [dispatch]);

  // Sync selected chatbot from URL param
  useEffect(() => {
    if (!loading && chatbots.length > 0) {
      if (params.chatbotId) {
        const match = chatbots.find((b) => b.id === params.chatbotId);
        if (match) { dispatch(selectChatbot(match)); return; }
      }
      if (!selected) dispatch(selectChatbot(chatbots[0]));
    }
  }, [chatbots, loading, params.chatbotId, dispatch, selected]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Fetch real usage for plan badge
  useEffect(() => {
    api.get("/billing/usage").then(setUsage).catch(() => {});
  }, []);

  const switchChatbot = (bot) => {
    dispatch(selectChatbot(bot));
    const parts = location.pathname.split("/");
    parts[parts.length - 1] = bot.id;
    navigate(parts.join("/"));
    setOpen(false);
  };

  const id = selected?.id;

  const planLabel = usage?.plan ?? "FREE";
  const msgsUsed  = usage?.messagesUsed ?? 0;
  const msgsLimit = usage?.messageLimit === -1 ? "∞" : (usage?.messageLimit ?? 500);
  const pct = usage?.messageLimit && usage.messageLimit !== -1
    ? Math.min(100, Math.round((msgsUsed / usage.messageLimit) * 100))
    : 0;

  return (
    <aside className="fixed top-16 left-0 w-64 h-[calc(100vh-4rem)] bg-white border-r flex flex-col text-sm text-gray-700">

      {/* ── Chatbot Switcher ── */}
      <div className="px-3 pt-4 pb-3 border-b" ref={ref}>
        <button
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition"
        >
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-indigo-400 text-white flex items-center justify-center text-sm font-bold shrink-0 shadow-sm">
            {selected?.icon || "💬"}
          </div>
          <div className="flex-1 min-w-0 text-left">
            <p className="text-sm font-semibold text-gray-900 truncate leading-tight">
              {loading ? "Loading…" : selected?.name || "No Chatbots"}
            </p>
            <p className="text-[11px] text-gray-400 leading-tight mt-0.5">Switch chatbot</p>
          </div>
          <FiChevronDown size={15} className={`shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {/* Dropdown */}
        {open && (
          <div className="absolute left-3 right-3 z-50 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
            <div className="px-3 pt-2.5 pb-1.5">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Your Chatbots</p>
            </div>
            <div className="max-h-52 overflow-y-auto">
              {loading ? (
                <p className="px-3 py-3 text-gray-400 text-xs">Loading…</p>
              ) : chatbots.length === 0 ? (
                <p className="px-3 py-3 text-gray-400 text-xs">No chatbots yet</p>
              ) : (
                chatbots.map((bot) => {
                  const isSel = selected?.id === bot.id;
                  return (
                    <button
                      key={bot.id}
                      onClick={() => switchChatbot(bot)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition ${isSel ? "bg-primary-light" : "hover:bg-gray-50"}`}
                    >
                      <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-primary to-indigo-400 text-white flex items-center justify-center text-xs font-bold shrink-0">
                        {bot.icon || "🤖"}
                      </div>
                      <span className={`flex-1 truncate text-sm ${isSel ? "font-semibold text-primary" : "text-gray-700"}`}>
                        {bot.name}
                      </span>
                      {isSel && <FiCheck size={13} className="text-primary shrink-0" />}
                    </button>
                  );
                })
              )}
            </div>
            <div className="border-t px-3 py-2">
              <button
                onClick={() => { setOpen(false); navigate("/chatbot/create"); }}
                className="w-full flex items-center gap-2 text-xs text-primary font-medium py-1.5 hover:underline"
              >
                <FiPlus size={13} /> Create new chatbot
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Nav ── */}
      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        {id ? (
          <>
            {/* Dashboard section */}
            <Section title="Dashboard">
              {NAV_ITEMS.filter((item) => canView(item.page, user)).map((item) => (
                <NavItem key={item.page} to={item.to(id)} icon={item.icon} label={item.label} />
              ))}
            </Section>

            {/* Sources — visible if user can view "sources" or is admin */}
            {canView("sources", user) && (
              <div>
                <button
                  onClick={() => setContent((v) => !v)}
                  className="w-full flex items-center justify-between mb-1.5 px-1"
                >
                  <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Sources</p>
                  <FiChevronRight size={12} className={`text-gray-400 transition-transform ${contentOpen ? "rotate-90" : ""}`} />
                </button>
                {contentOpen && (
                  <div className="space-y-0.5">
                    {SOURCE_ITEMS.map((item) => (
                      <NavItem key={item.label} to={item.to(id)} icon={item.icon} label={item.label} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Customize */}
            {CUSTOMIZE_ITEMS.some((item) => canView(item.page, user)) && (
              <Section title="Customize">
                {CUSTOMIZE_ITEMS.filter((item) => canView(item.page, user)).map((item) => (
                  <NavItem key={item.page} to={item.to(id)} icon={item.icon} label={item.label} />
                ))}
              </Section>
            )}

            {/* Integrations + Settings */}
            {OTHER_ITEMS.some((item) => canView(item.page, user)) && (
              <Section title="Advanced">
                {OTHER_ITEMS.filter((item) => canView(item.page, user)).map((item) => (
                  <NavItem key={item.page} to={item.to(id)} icon={item.icon} label={item.label} />
                ))}
              </Section>
            )}

            {/* Team — OWNER/ADMIN only */}
            {isAdmin && (
              <Section title="Organisation">
                <NavItem to="/team" icon={FiUserPlus} label="Team Members" />
              </Section>
            )}
          </>
        ) : !loading ? (
          <div className="px-1 mt-2">
            <p className="text-xs text-gray-400 mb-3">No chatbot selected. Create your first one to get started.</p>
            <button
              onClick={() => navigate("/chatbot/create")}
              className="flex items-center gap-2 text-xs text-white bg-primary px-3 py-2 rounded-lg font-medium hover:opacity-90"
            >
              <FiPlus size={13} /> Create chatbot
            </button>
          </div>
        ) : (
          <p className="text-xs text-gray-400 px-1 mt-2">Loading…</p>
        )}
      </div>

      {/* ── Plan badge ── */}
      <div className="px-3 pb-4 pt-2 border-t">
        <div className="px-3 py-2.5 rounded-xl bg-gradient-to-r from-primary/5 to-indigo-50">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-xs font-semibold text-gray-800 capitalize">{planLabel} Plan</p>
            {planLabel !== "ENTERPRISE" && (
              <button onClick={() => navigate("/billing")} className="text-[11px] font-semibold text-primary hover:underline">
                Upgrade
              </button>
            )}
          </div>
          <p className="text-[10px] text-gray-500 mb-1.5">
            {msgsUsed.toLocaleString()} / {typeof msgsLimit === "number" ? msgsLimit.toLocaleString() : msgsLimit} msgs this month
          </p>
          {usage?.messageLimit && usage.messageLimit !== -1 && (
            <div className="w-full bg-gray-200 rounded-full h-1">
              <div
                className={`h-1 rounded-full transition-all ${pct >= 90 ? "bg-red-400" : "bg-primary"}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          )}
        </div>
      </div>

    </aside>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest px-1 mb-1.5">{title}</p>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function NavItem({ to, icon: Icon, label, badge, badgeColor = "bg-primary/10 text-primary" }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${
          isActive
            ? "bg-primary text-white shadow-sm shadow-primary/20"
            : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon size={15} className={isActive ? "text-white" : "text-gray-400"} />
          <span className={`flex-1 text-sm ${isActive ? "font-semibold" : "font-medium"}`}>{label}</span>
          {badge && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold leading-none ${isActive ? "bg-white/20 text-white" : badgeColor}`}>
              {badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}
